import { Inject, Injectable } from '@angular/core';

import { BetslipConfig } from '@frontend/sports/common/client-config-data-access';
import { TrackCrossSaleData, TrackData, TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';

import { BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN, IBetslipMessagesProviderService } from '../../../base/services/betslip-messages-provider.service';
import { BetslipType } from '../../../core/betslip-type';
import { BetslipPick } from '../../../core/picks/betslip-pick';
import { IRewardToken } from '../../reward-tokens/reward-tokens.model';
import { getTokenTrackingType } from '../../reward-tokens/services/reward-tokens-tracking.utils';
import { LinearSystemBetTracking } from '../../system-bet/models';
import {
    BetslipLinearTracking,
    InfoTrackingContext,
    LinearCheckboxTrackingState,
    LinearCtaTrackingType,
    LinearModuleExpansionTrackingState,
} from '../models';
import { BetslipTrackingData } from '../tracking.models';
import { BetslipTrackingHelper } from './betslip-tracking-helper';

@Injectable()
export class BetslipLinearTrackingService {
    constructor(
        protected trackingService: TrackingService,
        @Inject(BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN) protected messageProvider: IBetslipMessagesProviderService,
        private betslipConfig: BetslipConfig,
    ) {}

    trackBetslipLoad(): void {
        const crossSaleData: TrackCrossSaleData = {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: BetslipLinearTracking.Betslip,
            [trackingConstants.COMPONENT_LABEL_EVENT]: BetslipLinearTracking.FullBetslip,
            [trackingConstants.COMPONENT_ACTION_EVENT]: BetslipLinearTracking.Load,
            [trackingConstants.COMPONENT_POSITION_EVENT]: BetslipLinearTracking.NotApplicable,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: this.getLocation(),
            [trackingConstants.COMPONENT_EVENT_DETAILS]: this.getLocation(),
            [trackingConstants.COMPONENT_URL_CLICKED]: BetslipLinearTracking.NotApplicable,
        };
        this.trackingService.track(trackingConstants.EVENT_CONTENT_VIEW, crossSaleData);
    }

    trackCheckboxToggle(betslipType: BetslipType, state: LinearCheckboxTrackingState): void {
        const crossSaleData: TrackCrossSaleData = {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: BetslipLinearTracking.Betslip,
            [trackingConstants.COMPONENT_LABEL_EVENT]: BetslipLinearTracking.FullBetslip,
            [trackingConstants.COMPONENT_ACTION_EVENT]: state,
            [trackingConstants.COMPONENT_POSITION_EVENT]: this.getPosition(betslipType),
            [trackingConstants.COMPONENT_LOCATION_EVENT]: this.getLocation(),
            [trackingConstants.COMPONENT_EVENT_DETAILS]: `checkbox`,
            [trackingConstants.COMPONENT_URL_CLICKED]: BetslipLinearTracking.NotApplicable,
        };
        this.trackingService.track(trackingConstants.EVENT_TRACKING, crossSaleData);
    }

    trackInfoIconClick(infoContext: InfoTrackingContext, additionalTypeContext?: string): void {
        const additionalContext = additionalTypeContext ? ` - ${additionalTypeContext}` : '';

        const crossSaleData: TrackCrossSaleData = {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: BetslipLinearTracking.Betslip,
            [trackingConstants.COMPONENT_LABEL_EVENT]: BetslipLinearTracking.FullBetslip,
            [trackingConstants.COMPONENT_ACTION_EVENT]: BetslipLinearTracking.Click,
            [trackingConstants.COMPONENT_POSITION_EVENT]: BetslipLinearTracking.NotApplicable,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: this.getLocation(),
            [trackingConstants.COMPONENT_EVENT_DETAILS]: `info icon - ${infoContext}${additionalContext}`,
            [trackingConstants.COMPONENT_URL_CLICKED]: BetslipLinearTracking.NotApplicable,
        };
        this.trackingService.track(trackingConstants.EVENT_TRACKING, crossSaleData);
    }

    trackPickListVisibilitySwitch(positionEvent: string, eventDetails: string): void {
        const crossSaleData: TrackCrossSaleData = {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: BetslipLinearTracking.Betslip,
            [trackingConstants.COMPONENT_LABEL_EVENT]: BetslipLinearTracking.FullBetslip,
            [trackingConstants.COMPONENT_ACTION_EVENT]: BetslipLinearTracking.Click,
            [trackingConstants.COMPONENT_POSITION_EVENT]: positionEvent,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: this.getLocation(),
            [trackingConstants.COMPONENT_EVENT_DETAILS]: eventDetails,
            [trackingConstants.COMPONENT_URL_CLICKED]: BetslipLinearTracking.NotApplicable,
        };
        this.trackingService.track(trackingConstants.EVENT_TRACKING, crossSaleData);
    }

    trackModuleExpansionToggle(betslipType: BetslipType, state: LinearModuleExpansionTrackingState): void {
        const crossSaleData: TrackCrossSaleData = {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: BetslipLinearTracking.Betslip,
            [trackingConstants.COMPONENT_LABEL_EVENT]: BetslipLinearTracking.FullBetslip,
            [trackingConstants.COMPONENT_ACTION_EVENT]: state,
            [trackingConstants.COMPONENT_POSITION_EVENT]: BetslipLinearTracking.NotApplicable,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: this.getLocation(),
            [trackingConstants.COMPONENT_EVENT_DETAILS]: this.getPosition(betslipType),
            [trackingConstants.COMPONENT_URL_CLICKED]: BetslipLinearTracking.NotApplicable,
        };
        this.trackingService.track(trackingConstants.EVENT_TRACKING, crossSaleData);
    }

    trackSytemBetTypeStakeClick(eventDetails: string): void {
        const crossSaleData: TrackCrossSaleData = {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: BetslipLinearTracking.Betslip,
            [trackingConstants.COMPONENT_LABEL_EVENT]: BetslipLinearTracking.FullBetslip,
            [trackingConstants.COMPONENT_ACTION_EVENT]: BetslipLinearTracking.Click,
            [trackingConstants.COMPONENT_POSITION_EVENT]: LinearSystemBetTracking.SystemBetPosition,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: this.getLocation(),
            [trackingConstants.COMPONENT_EVENT_DETAILS]: eventDetails,
            [trackingConstants.COMPONENT_URL_CLICKED]: BetslipLinearTracking.NotApplicable,
        };
        this.trackingService.track(trackingConstants.EVENT_TRACKING, crossSaleData);
    }

    trackStakeClick(position: BetslipType, eventDetails: string): void {
        const positionDetails = this.getPosition(position);

        const crossSaleData: TrackCrossSaleData = {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: BetslipLinearTracking.Betslip,
            [trackingConstants.COMPONENT_LABEL_EVENT]: BetslipLinearTracking.FullBetslip,
            [trackingConstants.COMPONENT_ACTION_EVENT]: BetslipLinearTracking.Click,
            [trackingConstants.COMPONENT_POSITION_EVENT]: positionDetails,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: this.getLocation(),
            [trackingConstants.COMPONENT_EVENT_DETAILS]: eventDetails,
            [trackingConstants.COMPONENT_URL_CLICKED]: BetslipLinearTracking.NotApplicable,
        };
        this.trackingService.track(trackingConstants.EVENT_TRACKING, crossSaleData);
    }

    trackSystemBankerBetClick(pick: BetslipPick): void {
        const crossSaleData: TrackData = {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: BetslipLinearTracking.Betslip,
            [trackingConstants.COMPONENT_LABEL_EVENT]: BetslipLinearTracking.FullBetslip,
            [trackingConstants.COMPONENT_ACTION_EVENT]: BetslipLinearTracking.Click,
            [trackingConstants.COMPONENT_POSITION_EVENT]: this.getPosition(BetslipType.System),
            [trackingConstants.COMPONENT_LOCATION_EVENT]: this.getLocation(),
            [trackingConstants.COMPONENT_EVENT_DETAILS]: `banker bet - ${pick.eventName.name}`,
            [trackingConstants.COMPONENT_URL_CLICKED]: BetslipLinearTracking.NotApplicable,
            [trackingConstants.MARKET_ID]: pick.market.id,
            [trackingConstants.SPORT_ID]: pick.sportId,
            [trackingConstants.SPORT_LEAGUE_ID]: BetslipTrackingHelper.getLeagueId(pick),
            [trackingConstants.RESULT_ID]: BetslipTrackingHelper.getResultId(pick),
        };
        this.trackingService.track(trackingConstants.EVENT_TRACKING, crossSaleData);
    }

    trackBetslipClientErrors(trackingData: BetslipTrackingData) {
        this.trackingService.track(trackingConstants.EVENT_CONTENT_VIEW, {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: BetslipLinearTracking.Betslip,
            [trackingConstants.COMPONENT_LABEL_EVENT]: BetslipLinearTracking.FullBetslip,
            [trackingConstants.COMPONENT_ACTION_EVENT]: BetslipLinearTracking.Load,
            [trackingConstants.COMPONENT_POSITION_EVENT]: trackingData.positionEvent,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: this.getLocation(),
            [trackingConstants.COMPONENT_EVENT_DETAILS]: trackingData.eventDetails,
            [trackingConstants.COMPONENT_URL_CLICKED]: BetslipLinearTracking.NotApplicable,
        });
    }

    trackSuccessMessageLoad(resultMessage: string): void {
        const crossSaleData: TrackCrossSaleData = {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: BetslipLinearTracking.Betslip,
            [trackingConstants.COMPONENT_LABEL_EVENT]: BetslipLinearTracking.FullBetslip,
            [trackingConstants.COMPONENT_ACTION_EVENT]: BetslipLinearTracking.Load,
            [trackingConstants.COMPONENT_POSITION_EVENT]: BetslipLinearTracking.SuccessMessage,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: this.getLocation(),
            [trackingConstants.COMPONENT_EVENT_DETAILS]: resultMessage,
            [trackingConstants.COMPONENT_URL_CLICKED]: BetslipLinearTracking.NotApplicable,
        };
        this.trackingService.track(trackingConstants.EVENT_CONTENT_VIEW, crossSaleData);
    }

    trackAddPromotionsClick(betslipType: BetslipType | undefined): void {
        const crossSaleData: TrackCrossSaleData = {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: BetslipLinearTracking.Betslip,
            [trackingConstants.COMPONENT_LABEL_EVENT]: BetslipLinearTracking.FullBetslip,
            [trackingConstants.COMPONENT_ACTION_EVENT]: BetslipLinearTracking.Click,
            [trackingConstants.COMPONENT_POSITION_EVENT]: this.getPosition(betslipType),
            [trackingConstants.COMPONENT_LOCATION_EVENT]: this.getLocation(),
            [trackingConstants.COMPONENT_EVENT_DETAILS]: BetslipLinearTracking.AddPromo,
            [trackingConstants.COMPONENT_URL_CLICKED]: BetslipLinearTracking.NotApplicable,
        };
        this.trackingService.track(trackingConstants.EVENT_TRACKING, crossSaleData);
    }

    trackPromotionsPopupLoad(betslipType: BetslipType | undefined): void {
        const crossSaleData: TrackCrossSaleData = {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: BetslipLinearTracking.Betslip,
            [trackingConstants.COMPONENT_LABEL_EVENT]: BetslipLinearTracking.FullBetslip,
            [trackingConstants.COMPONENT_ACTION_EVENT]: BetslipLinearTracking.Load,
            [trackingConstants.COMPONENT_POSITION_EVENT]: this.getPosition(betslipType),
            [trackingConstants.COMPONENT_LOCATION_EVENT]: this.getLocation(),
            [trackingConstants.COMPONENT_EVENT_DETAILS]: BetslipLinearTracking.PromotionsPopup,
            [trackingConstants.COMPONENT_URL_CLICKED]: BetslipLinearTracking.NotApplicable,
        };
        this.trackingService.track(trackingConstants.EVENT_CONTENT_VIEW, crossSaleData);
    }

    trackClosePromotionsPopupClick(betslipType: BetslipType | undefined): void {
        const crossSaleData: TrackCrossSaleData = {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: BetslipLinearTracking.Betslip,
            [trackingConstants.COMPONENT_LABEL_EVENT]: BetslipLinearTracking.FullBetslip,
            [trackingConstants.COMPONENT_ACTION_EVENT]: BetslipLinearTracking.Close,
            [trackingConstants.COMPONENT_POSITION_EVENT]: this.getPosition(betslipType),
            [trackingConstants.COMPONENT_LOCATION_EVENT]: this.getLocation(),
            [trackingConstants.COMPONENT_EVENT_DETAILS]: BetslipLinearTracking.PromotionsPopup,
            [trackingConstants.COMPONENT_URL_CLICKED]: BetslipLinearTracking.NotApplicable,
        };
        this.trackingService.track(trackingConstants.EVENT_TRACKING, crossSaleData);
    }

    trackTokenSelectionClick(betslipType: BetslipType | undefined, token: IRewardToken): void {
        const tokenData = getTokenTrackingType(token.rewardTokenType, token.customTitle);

        const crossSaleData: TrackCrossSaleData = {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: BetslipLinearTracking.Betslip,
            [trackingConstants.COMPONENT_LABEL_EVENT]: BetslipLinearTracking.FullBetslip,
            [trackingConstants.COMPONENT_ACTION_EVENT]: BetslipLinearTracking.Click,
            [trackingConstants.COMPONENT_POSITION_EVENT]: this.getPosition(betslipType),
            [trackingConstants.COMPONENT_LOCATION_EVENT]: this.getLocation(),
            [trackingConstants.COMPONENT_EVENT_DETAILS]: `${BetslipLinearTracking.PromotionsPopup} - ${tokenData}`,
            [trackingConstants.COMPONENT_URL_CLICKED]: BetslipLinearTracking.NotApplicable,
        };
        this.trackingService.track(trackingConstants.EVENT_TRACKING, crossSaleData);
    }

    trackSettingsIconClick(): void {
        const crossSaleData: TrackCrossSaleData = {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: BetslipLinearTracking.Betslip,
            [trackingConstants.COMPONENT_LABEL_EVENT]: BetslipLinearTracking.FullBetslip,
            [trackingConstants.COMPONENT_ACTION_EVENT]: BetslipLinearTracking.Click,
            [trackingConstants.COMPONENT_POSITION_EVENT]: BetslipLinearTracking.NotApplicable,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: this.getLocation(),
            [trackingConstants.COMPONENT_EVENT_DETAILS]: BetslipLinearTracking.SettingsIcon,
            [trackingConstants.COMPONENT_URL_CLICKED]: BetslipLinearTracking.NotApplicable,
        };
        this.trackingService.track(trackingConstants.EVENT_TRACKING, crossSaleData);
    }

    trackSettingsLoad(): void {
        const crossSaleData: TrackCrossSaleData = {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: BetslipLinearTracking.Betslip,
            [trackingConstants.COMPONENT_LABEL_EVENT]: BetslipLinearTracking.FullBetslip,
            [trackingConstants.COMPONENT_ACTION_EVENT]: BetslipLinearTracking.Load,
            [trackingConstants.COMPONENT_POSITION_EVENT]: BetslipLinearTracking.NotApplicable,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: this.getLocation(),
            [trackingConstants.COMPONENT_EVENT_DETAILS]: BetslipLinearTracking.SettingsPopup,
            [trackingConstants.COMPONENT_URL_CLICKED]: BetslipLinearTracking.NotApplicable,
        };
        this.trackingService.track(trackingConstants.EVENT_CONTENT_VIEW, crossSaleData);
    }

    trackSettingsCloseClick(): void {
        const crossSaleData: TrackCrossSaleData = {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: BetslipLinearTracking.Betslip,
            [trackingConstants.COMPONENT_LABEL_EVENT]: BetslipLinearTracking.FullBetslip,
            [trackingConstants.COMPONENT_ACTION_EVENT]: BetslipLinearTracking.Close,
            [trackingConstants.COMPONENT_POSITION_EVENT]: BetslipLinearTracking.NotApplicable,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: this.getLocation(),
            [trackingConstants.COMPONENT_EVENT_DETAILS]: BetslipLinearTracking.Settings,
            [trackingConstants.COMPONENT_URL_CLICKED]: BetslipLinearTracking.NotApplicable,
        };
        this.trackingService.track(trackingConstants.EVENT_TRACKING, crossSaleData);
    }

    trackBetslipCtaClick(ctaType: LinearCtaTrackingType): void {
        const crossSaleData: TrackCrossSaleData = {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: BetslipLinearTracking.Betslip,
            [trackingConstants.COMPONENT_LABEL_EVENT]: BetslipLinearTracking.FullBetslip,
            [trackingConstants.COMPONENT_ACTION_EVENT]: BetslipLinearTracking.Click,
            [trackingConstants.COMPONENT_POSITION_EVENT]: BetslipLinearTracking.NotApplicable,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: this.getLocation(),
            [trackingConstants.COMPONENT_EVENT_DETAILS]: ctaType,
            [trackingConstants.COMPONENT_URL_CLICKED]: BetslipLinearTracking.NotApplicable,
        };
        this.trackingService.track(trackingConstants.EVENT_TRACKING, crossSaleData);
    }

    private getLocation(): string {
        return this.betslipConfig.linearModeCheckboxesEnabled ? BetslipLinearTracking.LinearCheckbox : BetslipLinearTracking.LinearNoCheckbox;
    }

    private getPosition(betslipType: BetslipType | undefined): string {
        switch (betslipType) {
            case BetslipType.Combo:
                return BetslipLinearTracking.Combo;

            case BetslipType.System:
                return BetslipLinearTracking.System;

            case BetslipType.EditBet:
                return BetslipLinearTracking.EditBet;

            case BetslipType.Teaser:
                return BetslipLinearTracking.Teaser;

            case BetslipType.Single:
                return BetslipLinearTracking.Single;

            case BetslipType.BetBuilder:
                return BetslipLinearTracking.BetBuilder;

            default:
                return '';
        }
    }
}
